import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { formatIQD, formatDate, todayISO } from "../lib/format";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import StatusBadge from "../components/StatusBadge";
import { Plus, Pencil } from "lucide-react";

const emptyR = { name: "", phone: "", status: "Active", notes: "" };

function RestaurantList() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyR);
  const load = () => api.get("/restaurants").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    try {
      if (editing) await api.put(`/restaurants/${editing.id}`, form);
      else await api.post("/restaurants", form);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  return (
    <div className="space-y-4">
      <div className="flex justify-end"><Button onClick={() => { setEditing(null); setForm(emptyR); setOpen(true); }} data-testid="btn-add-rest" className="bg-emerald-600 hover:bg-emerald-700"><Plus size={16} className="mr-1" />Add Restaurant</Button></div>
      <Card className="p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50"><tr className="text-left text-xs uppercase text-muted-foreground"><th className="p-3">Name</th><th className="p-3">Phone</th><th className="p-3">Status</th><th className="p-3">Notes</th><th className="p-3 text-right"></th></tr></thead>
          <tbody>
            {list.map((r) => (
              <tr key={r.id} className="border-t table-row-hover" data-testid={`rest-row-${r.id}`}>
                <td className="p-3 font-medium">{r.name}</td>
                <td className="p-3 mono text-muted-foreground">{r.phone || "—"}</td>
                <td className="p-3"><StatusBadge status={r.status} /></td>
                <td className="p-3 text-muted-foreground truncate max-w-xs">{r.notes || "—"}</td>
                <td className="p-3 text-right"><Button variant="ghost" size="sm" onClick={() => { setEditing(r); setForm({ name: r.name, phone: r.phone || "", status: r.status, notes: r.notes || "" }); setOpen(true); }}><Pencil size={14} className="mr-1" />Edit</Button></td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">No restaurants yet.</td></tr>}
          </tbody>
        </table>
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit Restaurant" : "Add Restaurant"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="rest-name" className="mt-1.5" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="rest-phone" className="mt-1.5" /></div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1.5" data-testid="rest-status"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Active">Active</SelectItem><SelectItem value="Inactive">Inactive</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-save-rest">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Reconciliation() {
  const [rows, setRows] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(todayISO());
  const [method, setMethod] = useState("Cash");
  const [notes, setNotes] = useState("");

  const load = () => api.get("/restaurant-reconciliation").then((r) => setRows(r.data));
  useEffect(() => { load(); api.get("/restaurants").then((r) => setRestaurants(r.data)); }, []);

  const openPay = (d) => { setTarget(d); setAmount(String(d.restaurant_outstanding)); setPayOpen(true); };
  const submit = async () => {
    if (Number(amount) <= 0) return toast.error("Invalid amount");
    try {
      await api.post("/restaurant-payments", {
        restaurant_id: target.restaurant_id, daily_order_id: target.id,
        payment_date: date, amount: Number(amount), payment_method: method, notes,
      });
      toast.success("Restaurant payment recorded"); setPayOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4">
      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left text-xs uppercase text-muted-foreground">
                <th className="p-3">Date</th><th className="p-3">Restaurant</th>
                <th className="p-3 text-right">Order Total</th>
                <th className="p-3 text-right">Emp Paid</th>
                <th className="p-3 text-right">Emp Outstanding</th>
                <th className="p-3 text-right">Rest Paid</th>
                <th className="p-3 text-right">Rest Outstanding</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t table-row-hover" data-testid={`recon-row-${r.id}`}>
                  <td className="p-3">{formatDate(r.order_date)}</td>
                  <td className="p-3 font-medium">{r.restaurant_name || "—"}</td>
                  <td className="p-3 text-right mono">{formatIQD(r.total_amount)}</td>
                  <td className="p-3 text-right mono text-emerald-700">{formatIQD(r.employee_paid)}</td>
                  <td className="p-3 text-right mono text-rose-700">{formatIQD(r.employee_outstanding)}</td>
                  <td className="p-3 text-right mono text-emerald-700">{formatIQD(r.restaurant_paid)}</td>
                  <td className="p-3 text-right mono text-rose-700 font-semibold">{formatIQD(r.restaurant_outstanding)}</td>
                  <td className="p-3 text-right">
                    {r.restaurant_id && r.restaurant_outstanding > 0 && (
                      <Button variant="outline" size="sm" onClick={() => openPay(r)} data-testid={`btn-pay-rest-${r.id}`}>Pay</Button>
                    )}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">No daily orders yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Pay Restaurant</DialogTitle></DialogHeader>
          {target && (
            <div className="space-y-3">
              <div className="text-sm text-muted-foreground">{target.restaurant_name} · {formatDate(target.order_date)}</div>
              <div className="rounded-lg bg-slate-100 dark:bg-slate-800 p-3">
                <div className="text-xs uppercase text-muted-foreground">Outstanding to restaurant</div>
                <div className="heading text-xl font-extrabold text-rose-700">{formatIQD(target.restaurant_outstanding)}</div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5" /></div>
                <div><Label>Method</Label>
                  <Select value={method} onValueChange={setMethod}>
                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="Cash">Cash</SelectItem><SelectItem value="Bank Transfer">Bank Transfer</SelectItem><SelectItem value="Other">Other</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Amount</Label><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1.5" /></div>
              <div><Label>Notes</Label><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1.5" /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setPayOpen(false)}>Cancel</Button><Button onClick={submit} className="bg-emerald-600 hover:bg-emerald-700">Save Payment</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Restaurants() {
  return (
    <div className="space-y-6" data-testid="restaurants-page">
      <div>
        <h1 className="heading text-2xl font-extrabold tracking-tight">Restaurants</h1>
        <p className="text-sm text-muted-foreground">Manage restaurants and reconcile what employees paid vs. what's owed to each restaurant.</p>
      </div>
      <Tabs defaultValue="list">
        <TabsList><TabsTrigger value="list" data-testid="tab-rest-list">Restaurants</TabsTrigger><TabsTrigger value="recon" data-testid="tab-rest-recon">Reconciliation</TabsTrigger></TabsList>
        <TabsContent value="list" className="mt-4"><RestaurantList /></TabsContent>
        <TabsContent value="recon" className="mt-4"><Reconciliation /></TabsContent>
      </Tabs>
    </div>
  );
}
